import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ShellDomainProvider, StorageDataService, UtilityService } from '../standalone-packages/ecap3/core';
import { TranslateService } from '@ngx-translate/core';
import { isArray, snakeCase } from 'lodash-es';
import { combineLatest, map, Observable, of, Subscriber } from 'rxjs';
import { SCConfig } from '../../sc-config/constant/sc-config.constant';
import { Filter } from '../models';
import { mergeAllObjects } from '../static-methods';
import { CommonService } from './common.service';
import { FileService } from './file.service';
import { GqlQueryBuilderService } from './gql-query-builder.service';
import { SignalrNotificationService } from '../standalone-packages/ecap3/signalr-notification';
import { PCXKeyValue } from '../models/domain/PCXKeyValue';
import { CaseCommonService } from '../../app-create-case/services/case-common.service';
import { UrlResolverService } from './url/url-resolver.service';

export interface MetaData {
    Name: string;
    Value?: string | number;
    Values?: object[];
}

@Injectable({
    providedIn: 'root'
})
export class ReportService {
    private readonly urlResolver = inject(UrlResolverService);
    documentationTemplateId = ShellDomainProvider['api'].DocumentationTemplateId;
    reportBodyTemplateId = '';
    public pdfUtilityProfilesId = '9ca5f36b-df00-446c-b33e-8ee89d8fcbd6';
    pageNumberText = 'Seite {0} von {1}';
    pageNumberTextByLanguage = {
        en: 'Page {0} of {1}',
        fr: 'Page {0} de {1}',
        de: 'Seite {0} von {1}',
        it: 'PAGINA {0} a {1}'
    };
    reportHeaderTemplateId: string;
    reportFooterTemplateId: string;
    commonOptions = SCConfig.HttpCommonOptions;
    today = new Date();
    dateString = this.today.getDate() + '.' + (this.today.getMonth() + 1) + '.' + this.today.getFullYear();
    urlHTML = ShellDomainProvider['api'].TemplateEngineService + 'Command/CreateFileWithFilteredSQLQuery'; // Use for creating HTML
    pdfGenerationUrl = ShellDomainProvider['api'].PdfGeneratorV2 + 'Commands/CreatePdfsFromHtml'; // For using new PDF generator
    generateCaseReportVersionUrl = `${this.urlResolver.getPcxCommandUrl()}GenerateCaseReport`;

    constructor(
        private http: HttpClient,
        private fileService: FileService,
        private commonService: CommonService,
        private utilityService: UtilityService,
        private translateService: TranslateService,
        public storageDataService: StorageDataService,
        private signalrNotificationService: SignalrNotificationService,
        private gqlQueryBuilderService: GqlQueryBuilderService,
        private caseCommonService: CaseCommonService
    ) {}

    prepareMetaData(metaDataList): MetaData[] {
        return Object.keys(metaDataList)
            .map(key => {
                const metaDatum = {} as MetaData;
                metaDatum.Name = key;
                if (typeof metaDataList[key] === 'object') {
                    metaDatum['Values'] = isArray(metaDataList[key]) ? metaDataList[key] : [metaDataList[key]];
                } else {
                    metaDatum['Value'] = metaDataList[key];
                }
                return metaDatum;
            })
            .concat([
                {
                    Name: 'TimezoneOffsetInMinutes',
                    Value: (new Date().getTimezoneOffset() * -1).toString()
                }
            ]);
    }

    /**
     * calls the pdfGeneratorV2 to generate the pdf from header, footer and body html
     * @param headerId Static HTML saved in DB which will be used as PDF header
     * @param footerId Static HTML saved in DB which will be used as PDF footer
     * @param fileId HTML file generated by TemplateEngine
     * @param pdfId PDF you want to generate
     * @param pdfName Name of the PDF file
     * @param pdfNotificationSubscriptionId NotificationFilterId to catch file generation notification
     * @param pdfGenerationProfile Profile saved in DB which will be used for PDF generation
     * @param engine Used pdf rendering engine, use 3 if other engine is not specifically needed
     * @param eventReferenceData ReferenceData for notification
     * @param pageNumberEnabled Whether page number is needed
     * @param totalPageNumberEnabled Whether total page number is needed
     * @param headerHeight
     * @param footerHeight
     * @private
     */

    generatePdf(
        headerId: string,
        footerId: string,
        fileId: string,
        pdfId: string,
        pdfName: string,
        pdfNotificationSubscriptionId: string,
        pdfGenerationProfile: string,
        engine = 3,
        eventReferenceData: Object,
        pageNumberEnabled = true,
        totalPageNumberEnabled = true,
        headerHeight = headerId ? 120 : 80,
        footerHeight = footerId ? 200 : 80,
        pageNumberText: string
    ) {
        const fileName = pdfName?.toLowerCase()?.endsWith('.pdf') ? pdfName : `${pdfName}.pdf`;

        const body = {
            MessageCoRelationId: pdfNotificationSubscriptionId,
            EventReferenceData: eventReferenceData,
            CreateFromHtmlCommands: [
                {
                    HtmlFileId: fileId,
                    FooterHtmlFileId: footerId,
                    HeaderHtmlFileId: headerId,
                    DirectoryId: null,
                    OutputPdfFileId: pdfId,
                    OutputPdfFileName: fileName,
                    HeaderHeight: headerHeight,
                    FooterHeight: footerHeight,
                    FirstPageHeaderFileId: null,
                    FirstPageFooterFileId: null,
                    IsPageNumberEnabled: pageNumberEnabled,
                    IsTotalPageCountEnabled: totalPageNumberEnabled,
                    UseFormatting: false,
                    Engine: engine,
                    Profile: pdfGenerationProfile,
                    HasHeader: !!headerId,
                    HasFooter: !!footerId,
                    HasFirstPageHeader: false,
                    HasFirstPageFooter: false,
                    OpenInBrowser: false,
                    PageNumberText: pageNumberText
                }
            ]
        };
        return this.http.post(this.pdfGenerationUrl, body, this.commonOptions);
    }

    /** calls the pdfGeneratorV2 to generate the pdf from header, footer and body html */
    generatePdfDataChecking(
        headerId: string,
        footerId: string,
        fileId: string,
        pdfId: string,
        pdfName: string,
        pdfNotificationSubscriptionId: string,
        pdfGenerationProfile: string,
        headerHeight = headerId ? 25 : 20,
        footerHeight = footerId ? 25 : 0,
        pdfType = 'invoice'
    ) {
        if (!headerHeight) headerHeight = headerId ? 30 : 20;
        if (!footerHeight) footerHeight = footerId ? 25 : 0;
        if (!pdfType) pdfType = 'invoice';
        return this.generatePdf(
            headerId,
            footerId,
            fileId,
            pdfId,
            pdfName,
            pdfNotificationSubscriptionId,
            pdfGenerationProfile,
            2,
            { type: pdfType == 'invoice' ? '3' : '2' },
            true,
            true,
            headerHeight,
            footerHeight,
            this.pageNumberText
        );
    }

    generateReportHtml(MeasureId, FileId, imageList, ProtocolId, TemplateId?) {
        if (!TemplateId) {
            TemplateId = this.documentationTemplateId;
        }
        const metaData = [
            {
                Name: 'Images',
                Values: imageList
            }
        ];
        return this.createFileWithFilteredSqlQuery(
            TemplateId,
            FileId,
            this.photoDocumentationQueryDataProcessor(MeasureId, ProtocolId),
            metaData
        );
    }

    photoDocumentationQueryDataProcessor(MeasureId, ProtocolId) {
        return [
            {
                EntityName: 'Measure',
                Text: `Select <ItemId, MeasureName, PlanYear>
                       from <Measure>
                       where <ItemId=__eql(${MeasureId})>`,
                Key: 'Measure'
            },
            {
                EntityName: 'Protocol',
                Text: `Select <ItemId, ProtocolName, MeasureId, Strecke, Direction>
                       from <Protocol>
                       where <ItemId=__eql(${ProtocolId})>`,
                Key: 'Protocol'
            }
        ];
    }

    generateHeaderHtml(headerHtmlId, metaDataList, headerTemplateId, subscriptionHeaderId?): Observable<any> {
        return this.createFileWithFilteredSqlQuery(
            headerTemplateId,
            headerHtmlId,
            [],
            metaDataList,
            subscriptionHeaderId
        );
    }

    generateFooterHtml(footerHtmlId, footerTemplateId): Observable<any> {
        const metaData: MetaData[] = [
            {
                Name: 'Date',
                Value: this.dateString
            }
        ];
        return this.createFileWithFilteredSqlQuery(footerTemplateId, footerHtmlId, [], metaData);
    }

    /**
     * @param templateId HTML file used for html generation
     * @param fileId HTML file generated by TemplateEngine
     * @param metaData Hardcoded data for HTML generation
     * @param filteredSqlQueryData Queries which TemplateEngine will fetch for HTML generation
     * @param subscriptionFilterId NotificationFilterId to catch file generation notification
     * @private
     */

    createFileWithFilteredSqlQuery(
        templateId: string,
        fileId: string,
        filteredSqlQueryData: object[] = [],
        metaData: MetaData[] = [],
        subscriptionFilterId?: string
    ): Observable<any> {
        const body = {
            TemplateFileId: templateId,
            FileId: fileId,
            FileNameExtension: '.html',
            SubscriptionFilterId: subscriptionFilterId || '12949826-8fce-45cc-a484-7505b71e3c12',
            RaiseEventOnProcessEnding: false,
            NotifyOnProcessEnding: true,
            FilteredSqlQueryDatas: filteredSqlQueryData,
            MetaDataList: metaData
        };
        return this.http.post(this.urlHTML, body, this.commonOptions);
    }

    generateExcelReport(
        payload: Object = {},
        filters: Filter[],
        sortBy = 'CreatedDate: 1',
        translations: { Prefix?: string; Keys: string[] }[],
        language
    ) {
        const translationObservables = translations.map(translation =>
            this.getMultipleTranslationForReport(translation.Keys, translation.Prefix, language)
        );
        return new Observable(obs => {
            combineLatest(translationObservables).subscribe(translationResponses => {
                Object.assign(payload, {
                    ReportFileName: this.getFileNameForReport('CaseList Report', payload['ReportFileId']),
                    FilterString: `{${this.gqlQueryBuilderService.prepareFilterString(filters)}}`,
                    TimezoneOffsetInMinutes: new Date().getTimezoneOffset() * -1,
                    Translations: mergeAllObjects(translationResponses),
                    SortBy: `{${sortBy}}`
                });
                this.http
                    .post(
                        this.urlResolver.getPcxCommandUrl() + 'ExportCaseListExcelReport',
                        payload,
                        SCConfig.HttpCommonOptions
                    )
                    .subscribe(res => this.completeObservable(obs, res));
            });
        });
    }

    createSubscriptionForPDFGeneration(pdfId, notificationId: string): Observable<any> {
        return new Observable(obs => {
            let firstSignal = true;
            this.signalrNotificationService.subscribeSingle(
                'PdfsFromHtmlCreatedEvent',
                notificationId,
                'PdfsFromHtmlCreatedEvent',
                (notification: any) => {
                    const notificationValue = JSON.parse(notification.value);
                    if (firstSignal) {
                        if (notificationValue.Response.OutputFileId) {
                            firstSignal = false;
                            this.fileService.downloadFileByFileId(pdfId);
                            this.completeObservable(obs, notificationValue);
                        } else {
                            console.warn('PDF generation failed', notification);
                            obs.error(notificationValue);
                        }
                    }
                }
            );
        });
    }

    templateUpload(file, fileId) {
        const Name = file.name;
        this.storageDataService.getPreSignedUrl(fileId, Name, '', ['file']).subscribe(response1 => {
            const url = response1.body.UploadUrl;
            this.storageDataService.uploadPresignedFile(url, file).subscribe(response2 => {});
        });
    }

    handleFileGenerationNotification(notification: { key: string; value: string }): boolean {
        const notificationValue = JSON.parse(notification.value);
        return notificationValue.StatusCode === 0;
    }

    handlePdfGenerationFailure(snackBar: MatSnackBar) {
        snackBar.dismiss();
        this.translateService.stream('PDF_GENERATION_FAILURE').subscribe(failureMessage => {
            snackBar.open(failureMessage, null, {
                duration: 5000,
                verticalPosition: 'top',
                horizontalPosition: 'end'
            });
        });
    }

    downloadReportAfterNotification(
        reportFileId: string,
        notificationSubscriptionId = reportFileId,
        context = 'Export',
        action = 'ExportReport'
    ): Observable<any> {
        return new Observable(obs => {
            const completeObservableAndClearInterval = () => {
                clearInterval(intervalId);
                obs.complete();
            };

            let intervalRepeatCount = 0;
            let firstSubscription = true;
            const intervalId = setInterval(() => {
                this.fileService.getFileById(reportFileId).subscribe(fileResponse => {
                    if (fileResponse && firstSubscription) {
                        firstSubscription = false;
                        clearInterval(intervalId);
                        this.fileService.createUrlWithObservableReturn(fileResponse).subscribe(res => {
                            obs.next(fileResponse);
                            completeObservableAndClearInterval();
                        });
                    } else if (intervalRepeatCount === 40 && firstSubscription) {
                        // After 20minutes
                        firstSubscription = false;
                        obs.next(null);
                        completeObservableAndClearInterval();
                    } else {
                        intervalRepeatCount++;
                    }
                });
            }, 30000);

            this.signalrNotificationService.subscribeSingle(
                context,
                notificationSubscriptionId,
                action,
                (notification: any) => {
                    const response = JSON.parse(notification['value']);
                    if (firstSubscription) {
                        firstSubscription = false;
                        clearInterval(intervalId);
                        if (response['Success']) {
                            this.fileService
                                .downloadFileByFileIdWithObservableWithRetry(reportFileId)
                                .subscribe(res => {
                                    obs.next(response);
                                    completeObservableAndClearInterval();
                                });
                        } else {
                            obs.next(null);
                            completeObservableAndClearInterval();
                        }
                    }
                }
            );
        });
    }

    public getFileNameForReport(translatedReportName: string, fileId: string, extension?: 'xlsx' | 'pdf') {
        return snakeCase(translatedReportName) + '-' + fileId + (extension ? `.${extension}` : '');
    }

    completeObservable(requestObservable: Subscriber<any>, value) {
        requestObservable.next(value);
        requestObservable.complete();
    }

    // TODO: remove, it isn't used anywhere This isn't being used anywhere, we now generate invoice through bexio
    // generateInvoice(metaDataList, PDFId, pdfNotificationSubscriptionId, FileName) {
    //     this.reportHeaderTemplateId = '85fc16b7-3f47-4c22-becb-24ce779d43d8';
    //     this.reportBodyTemplateId = 'bfc08769-2fc5-44a1-a0df-a2511884ada3';
    //     this.pageNumberText = this.pageNumberTextByLanguage[this.translateService.currentLang];
    //     const templateId = this.reportBodyTemplateId;
    //     const subscriptionFilterId = this.utilityService.getNewGuid();
    //     const metaData = this.prepareMetaData(metaDataList);
    //     const headerHtmlId = '';
    //     const FileId = this.utilityService.getNewGuid();
    //     const footerHtmlId = '';
    //     const reportName = FileName;

    //     const firstNotification = {
    //         firstHeaderNotification: true,
    //         firstBodyNotification: true
    //     };
    //     const timer = {
    //         timerForHeaderHtml: 0,
    //         timerForBodyHtml: 0
    //     };

    //     return new Observable(requestObservable => {
    //         this.signalrNotificationService.subscribeSingle(
    //             'GenerateCompiledFile',
    //             subscriptionFilterId,
    //             'compile',
    //             (notification: any) => {
    //                 const fileGenerated = this.handleFileGenerationNotification(notification);
    //                 if (fileGenerated) {
    //                     if (firstNotification.firstBodyNotification) {
    //                         firstNotification.firstBodyNotification = false;
    //                         clearTimeout(timer.timerForBodyHtml);
    //                         this.generatePdfDataChecking(
    //                             headerHtmlId,
    //                             footerHtmlId,
    //                             FileId,
    //                             PDFId,
    //                             reportName,
    //                             pdfNotificationSubscriptionId,
    //                             this.pdfUtilityProfilesId
    //                         ).subscribe(response => {
    //                             this.completeObservable(requestObservable, response);
    //                             clearTimeout(timer.timerForBodyHtml);
    //                             clearTimeout(timer.timerForHeaderHtml);
    //                         });
    //                     }
    //                 } else {
    //                     firstNotification.firstHeaderNotification = false;
    //                     firstNotification.firstBodyNotification = false;
    //                     clearTimeout(timer.timerForBodyHtml);
    //                     clearTimeout(timer.timerForHeaderHtml);
    //                     this.completeObservable(requestObservable, { StatusCode: 503 });
    //                 }
    //             }
    //         );
    //         this.createFileWithFilteredSqlQuery(templateId, FileId, [], metaData, subscriptionFilterId).subscribe(
    //             res => {}
    //         );
    //         this.generatePdfAfterGeneratedBodyHtml(
    //             headerHtmlId,
    //             footerHtmlId,
    //             FileId,
    //             PDFId,
    //             reportName,
    //             pdfNotificationSubscriptionId,
    //             this.pdfUtilityProfilesId,
    //             null,
    //             null,
    //             'invoice',
    //             requestObservable,
    //             15,
    //             firstNotification,
    //             timer
    //         );
    //     });
    // }

    generateBodyHtml(
        templateId: string,
        fileId: string,
        filteredSqlQueryData: object[] = [],
        metaData: MetaData[] = [],
        subscriptionFilterId?: string
    ) {
        this.createFileWithFilteredSqlQuery(
            templateId,
            fileId,
            filteredSqlQueryData,
            metaData,
            subscriptionFilterId
        ).subscribe(res3 => {});
    }

    generateBodyAfterGeneratedHeaderHtml(
        headerId,
        templateId: string,
        fileId: string,
        filteredSqlQueryData: object[] = [],
        metaData: MetaData[] = [],
        subscriptionFilterId?: string,
        requestObservable?,
        checkingCounter?,
        firstNotification?: { firstHeaderNotification: boolean; firstBodyNotification: boolean },
        timer?: { timerForHeaderHtml: any; timerForBodyHtml: any }
    ) {
        if (!firstNotification || !firstNotification.firstHeaderNotification || !checkingCounter) return;
        clearTimeout(timer.timerForHeaderHtml);
        timer.timerForHeaderHtml = setTimeout(() => {
            if (firstNotification.firstHeaderNotification) {
                this.fileService.getFileById(headerId).subscribe(file => {
                    if (firstNotification.firstHeaderNotification && file && file.ItemId) {
                        firstNotification.firstHeaderNotification = false;
                        this.generateBodyHtml(templateId, fileId, filteredSqlQueryData, metaData, subscriptionFilterId);
                    } else if (firstNotification.firstHeaderNotification && checkingCounter - 1 > 0) {
                        this.generateBodyAfterGeneratedHeaderHtml(
                            headerId,
                            templateId,
                            fileId,
                            filteredSqlQueryData,
                            metaData,
                            subscriptionFilterId,
                            requestObservable,
                            checkingCounter - 1,
                            firstNotification,
                            timer
                        );
                    } else if (firstNotification.firstHeaderNotification) {
                        firstNotification.firstHeaderNotification = false;
                        firstNotification.firstBodyNotification = false;
                        clearTimeout(timer.timerForBodyHtml);
                        clearTimeout(timer.timerForHeaderHtml);
                        this.completeObservable(requestObservable, { StatusCode: 503 });
                    }
                });
            }
        }, 2500);
    }

    generateCaseReport(
        metaDataList,
        PDFId: string,
        pdfNotificationSubscriptionId: string,
        FileName: string,
        reportTemplateId: string,
        companyType,
        lang: string
    ) {
        this.reportHeaderTemplateId = '85fc16b7-3f47-4c22-becb-24ce779d43d8';
        this.reportBodyTemplateId = reportTemplateId;
        this.pageNumberText = this.pageNumberTextByLanguage[lang];
        const templateId = this.reportBodyTemplateId;
        const subscriptionFilterId = this.utilityService.getNewGuid();
        const metaData = this.prepareMetaData(metaDataList);
        const headerData = {
            CompanyLogoId: companyType == 'real_estate' ? 'a1efff75-c643-4395-b710-dca9871270ea' : '',
            DepartmentInfo: metaDataList['DepartmentInfo']
        };
        const headerMetadata = [
            {
                Name: 'headerContent',
                Values: [headerData]
            }
        ];

        const headerHtmlId = this.utilityService.getNewGuid();
        const fileId = this.utilityService.getNewGuid();
        const footerHtmlId = '';
        const reportName = FileName;
        const subscriptionHeaderId = this.utilityService.getNewGuid();
        const firstNotification = {
            firstHeaderNotification: true,
            firstBodyNotification: true
        };
        const timer = {
            timerForHeaderHtml: 0,
            timerForBodyHtml: 0
        };

        this.signalrNotificationService.subscribeSingle(
            'GenerateCompiledFile',
            subscriptionHeaderId,
            'compile',
            (notification: any) => {
                const fileGenerated = this.handleFileGenerationNotification(notification);
                if (firstNotification.firstHeaderNotification && fileGenerated) {
                    firstNotification.firstHeaderNotification = false;
                    clearTimeout(timer.timerForHeaderHtml);
                    this.generateBodyHtml(templateId, fileId, [], metaData, subscriptionFilterId);
                }
            }
        );

        return new Observable(requestObservable => {
            this.signalrNotificationService.subscribeSingle(
                'GenerateCompiledFile',
                subscriptionFilterId,
                'compile',
                (notification: any) => {
                    const fileGenerated = this.handleFileGenerationNotification(notification);
                    if (fileGenerated) {
                        if (firstNotification.firstBodyNotification) {
                            firstNotification.firstBodyNotification = false;
                            clearTimeout(timer.timerForBodyHtml);
                            this.generatePdfDataChecking(
                                headerHtmlId,
                                footerHtmlId,
                                fileId,
                                PDFId,
                                reportName,
                                pdfNotificationSubscriptionId,
                                this.pdfUtilityProfilesId,
                                35,
                                30,
                                'report'
                            ).subscribe(response => {
                                this.completeObservable(requestObservable, response);
                                clearTimeout(timer.timerForBodyHtml);
                                clearTimeout(timer.timerForHeaderHtml);
                            });
                        }
                    } else {
                        firstNotification.firstHeaderNotification = false;
                        firstNotification.firstBodyNotification = false;
                        clearTimeout(timer.timerForBodyHtml);
                        clearTimeout(timer.timerForHeaderHtml);
                        this.completeObservable(requestObservable, { StatusCode: 503 });
                    }
                }
            );
            this.generateHeaderHtml(
                headerHtmlId,
                headerMetadata,
                this.reportHeaderTemplateId,
                subscriptionHeaderId
            ).subscribe(res1 => {
                this.generateBodyAfterGeneratedHeaderHtml(
                    headerHtmlId,
                    templateId,
                    fileId,
                    [],
                    metaData,
                    subscriptionFilterId,
                    requestObservable,
                    10,
                    firstNotification,
                    timer
                );
            });
            this.generatePdfAfterGeneratedBodyHtml(
                headerHtmlId,
                footerHtmlId,
                fileId,
                PDFId,
                reportName,
                pdfNotificationSubscriptionId,
                this.pdfUtilityProfilesId,
                35,
                30,
                'report',
                requestObservable,
                15,
                firstNotification,
                timer
            );
        });
    }

    /** this is called after header and footer html is generated. */
    generatePdfAfterGeneratedBodyHtml(
        headerId: string,
        footerId: string,
        fileId: string,
        pdfId: string,
        pdfName: string,
        pdfNotificationSubscriptionId: string,
        pdfGenerationProfile: string,
        headerHeight,
        footerHeight,
        pdfType,
        requestObservable?,
        checkingCounter?,
        firstNotification?: { firstHeaderNotification: boolean; firstBodyNotification: boolean },
        timer?: { timerForHeaderHtml: any; timerForBodyHtml: any }
    ) {
        if (!firstNotification || !firstNotification.firstBodyNotification || !checkingCounter) return;
        clearTimeout(timer.timerForBodyHtml);
        timer.timerForBodyHtml = setTimeout(() => {
            if (firstNotification.firstBodyNotification) {
                this.fileService.getFileById(fileId).subscribe(file => {
                    if (firstNotification.firstBodyNotification && file && file.ItemId) {
                        firstNotification.firstBodyNotification = false;
                        this.generatePdfDataChecking(
                            headerId,
                            footerId,
                            fileId,
                            pdfId,
                            pdfName,
                            pdfNotificationSubscriptionId,
                            pdfGenerationProfile,
                            headerHeight,
                            footerHeight,
                            pdfType
                        ).subscribe(response => {
                            this.completeObservable(requestObservable, response);
                            return;
                        });
                    } else if (firstNotification.firstBodyNotification && checkingCounter - 1 > 0) {
                        this.generatePdfAfterGeneratedBodyHtml(
                            headerId,
                            footerId,
                            fileId,
                            pdfId,
                            pdfName,
                            pdfNotificationSubscriptionId,
                            pdfGenerationProfile,
                            headerHeight,
                            footerHeight,
                            pdfType,
                            requestObservable,
                            checkingCounter - 1,
                            firstNotification,
                            timer
                        );
                    } else if (firstNotification.firstBodyNotification) {
                        firstNotification.firstHeaderNotification = false;
                        firstNotification.firstBodyNotification = false;
                        clearTimeout(timer.timerForBodyHtml);
                        clearTimeout(timer.timerForHeaderHtml);
                        this.completeObservable(requestObservable, { StatusCode: 503 });
                    }
                });
            }
        }, 4000);
    }

    getMultipleTranslationForReport(
        translationKeys: string[],
        prefix: string = null,
        lang: string
    ): Observable<{ [key: string]: string }> {
        const keys = translationKeys.map(key => `${prefix ? prefix + '.' : ''}${key}`);
        var translatedValue = this.translateService.getParsedResult(this.translateService.translations[lang], keys, {
            LiabilityEvaluationMinimumThresholdPercentage:
                this.caseCommonService?.currentCaseInsuranceCompany?.LiabilityEvaluationMinimumThresholdPercentage ??
                '-'
        });
        const translation: { [key: string]: string } = {};
        translationKeys.forEach(key => {
            translation[key] = translatedValue[`${prefix ? prefix + '.' : ''}${key}`];
        });
        return of(translation);
    }

    getMultipleTranslationForReport2(translationKeys: string[], prefix: string = null, lang: string): PCXKeyValue[] {
        let translations: PCXKeyValue[] = [];
        const keys = translationKeys.map(key => `${prefix ? prefix + '.' : ''}${key}`);
        var translatedValue = this.translateService.getParsedResult(this.translateService.translations[lang], keys, {
            LiabilityEvaluationMinimumThresholdPercentage:
                this.caseCommonService?.currentCaseInsuranceCompany?.LiabilityEvaluationMinimumThresholdPercentage ??
                '-'
        });
        translationKeys.forEach(key => {
            let translation: PCXKeyValue = new PCXKeyValue();
            translation.Key = key;
            translation.Value = translatedValue[`${prefix ? prefix + '.' : ''}${key}`];
            translations.push(translation);
        });
        return translations;
    }

    // 23-08-2023 - NO reference
    // getMultipleTranslationForReportSync(translationKeys: string[], prefix: string = null) {
    //     const response = {};
    //     translationKeys.forEach(key => {
    //         response[key] = this.translateService.instant(`${prefix ? prefix + '.' : ''}${key}`);
    //     });
    //     return response;
    // }

    // 23-08-2023 - NO reference
    // private generateReportHeader(fileId: string, templateId: string, reportName: string) {
    //     const metaData = [
    //         {
    //             Name: 'RISK_REPORT',
    //             Value: reportName
    //         }
    //     ];
    //     return this.createFileWithFilteredSqlQuery(templateId, fileId, [], metaData);
    // }
    generateCaseReportVersion2(payload: { InsuranceCaseId: string }) {
        return this.http
            .post(this.generateCaseReportVersionUrl, payload, {
                headers: SCConfig.HttpCommonHeader,
                withCredentials: true,
                observe: 'response'
            })
            .pipe(
                map((response: any) => {
                    return response.body;
                })
            );
    }
}
